package no.vareflow.app.domain

/**
 * Support between the people in a company and the owner (Garasjestudio): Mer → Support sends a
 * message, the owner answers on the Eier page, both see the conversation. Firestore: support/{id}
 * (one conversation) and support/{id}/messages/{id}.
 */
enum class SupportCategory(private val nb: String, private val en: String) {
    BUG("Feil", "Bug"),
    QUESTION("Spørsmål", "Question"),
    WISH("Ønske", "Request");

    val label: String get() = t(nb, en)
}

enum class SupportStatus(private val nb: String, private val en: String) {
    /** Waiting for the owner. */
    OPEN("Venter på svar", "Waiting for reply"),
    /** The owner has answered. */
    ANSWERED("Besvart", "Answered"),
    CLOSED("Lukket", "Closed");

    val label: String get() = t(nb, en)
}

data class SupportTicket(
    val id: String,
    val companyId: String,
    val companyName: String,
    val uid: String,
    val userName: String,
    val userEmail: String,
    val category: SupportCategory,
    val subject: String,
    val status: SupportStatus = SupportStatus.OPEN,
    /** New message from the user that the owner has not opened yet. */
    val unreadByOwner: Boolean = true,
    /** New answer from the owner that the user has not opened yet. */
    val unreadByUser: Boolean = false,
    val createdAt: Long = 0,
    val updatedAt: Long = 0,
    /** Filled in automatically, so the owner does not have to ask. */
    val appVersion: String = "",
    val platform: String = "",
    val language: String = ""
)

data class SupportMessage(
    val id: String,
    /** "user" or "owner". */
    val from: String,
    val authorName: String,
    val text: String,
    val createdAt: Long = 0
) {
    val fromOwner: Boolean get() = from == FROM_OWNER

    companion object {
        const val FROM_USER = "user"
        const val FROM_OWNER = "owner"
    }
}

object SupportRules {
    const val MAX_SUBJECT = 120
    const val MAX_TEXT = 4000

    /** Why this message cannot be sent, or null. */
    fun problem(subject: String?, text: String): String? = when {
        subject != null && subject.isBlank() -> t("Skriv et kort emne.", "Write a short subject.")
        subject != null && subject.trim().length > MAX_SUBJECT -> t("Emnet er for langt (høyst $MAX_SUBJECT tegn).", "The subject is too long (at most $MAX_SUBJECT characters).")
        text.isBlank() -> t("Skriv en melding.", "Write a message.")
        text.trim().length > MAX_TEXT -> t("Meldingen er for lang (høyst $MAX_TEXT tegn).", "The message is too long (at most $MAX_TEXT characters).")
        else -> null
    }

    /** Owner's list: unread first, then open ones, then newest. */
    fun ownerOrder(tickets: List<SupportTicket>): List<SupportTicket> =
        tickets.sortedWith(compareByDescending<SupportTicket> { it.unreadByOwner }
            .thenBy { if (it.status == SupportStatus.CLOSED) 1 else 0 }
            .thenByDescending { it.updatedAt })
}
